import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Calculator, BadgeCheck } from "lucide-react";
import { SEO_CITIES } from "@/lib/seoCities";

const SITE = "https://clickandgard.fr";
const PATH = "/tarifs-baby-sitting";

const CITY_RATES: Array<{ city: string; slug: string; low: number; high: number }> = [
  { city: "Paris", slug: "paris", low: 13, high: 18 },
  { city: "Lyon", slug: "lyon", low: 12, high: 16 },
  { city: "Marseille", slug: "marseille", low: 11, high: 15 },
  { city: "Toulouse", slug: "toulouse", low: 11, high: 15 },
  { city: "Bordeaux", slug: "bordeaux", low: 12, high: 16 },
  { city: "Lille", slug: "lille", low: 11, high: 14 },
  { city: "Nantes", slug: "nantes", low: 11, high: 15 },
  { city: "Nice", slug: "nice", low: 12, high: 16 },
];

const SITUATIONS: Array<{ label: string; rate: string; detail: string }> = [
  { label: "Garde ponctuelle en soirée", rate: "11 € – 15 € / h", detail: "1 à 2 enfants, quelques heures après le dîner, souvent le week-end." },
  { label: "Sortie d'école & périscolaire", rate: "12 € – 16 € / h", detail: "Récupération à l'école, goûter, devoirs et bain jusqu'au retour des parents." },
  { label: "Garde régulière à domicile", rate: "12 € – 17 € / h", detail: "Plusieurs créneaux par semaine, planning fixe, souvent déclaré en CESU." },
  { label: "Garde de nuit ou week-end complet", rate: "13 € – 20 € / h", detail: "Horaires atypiques, majoration fréquente le dimanche et les jours fériés." },
  { label: "Fratrie (3 enfants et plus)", rate: "+1 € à +3 € / h", detail: "Supplément usuel appliqué par enfant supplémentaire." },
];

const FAQ: Array<{ q: string; a: string }> = [
  {
    q: "Quel est le tarif horaire d'une baby-sitter en France ?",
    a: "Le tarif d'une baby-sitter se situe le plus souvent entre 11 € et 15 € de l'heure en net. À Paris et dans les grandes métropoles, la fourchette monte à 13 € – 18 €. Le prix dépend de la ville, de l'expérience, des diplômes (CAP Petite Enfance, BAFA, PSC1) et du nombre d'enfants.",
  },
  {
    q: "Quel est le tarif minimum légal d'un baby-sitter ?",
    a: "La rémunération ne peut pas être inférieure au SMIC horaire net applicable aux salariés du particulier employeur, majoré de 10 % de congés payés. En pratique, les baby-sitters expérimentées se situent nettement au-dessus de ce minimum.",
  },
  {
    q: "Faut-il payer plus cher le soir, le week-end ou les jours fériés ?",
    a: "Ce n'est pas obligatoire pour une garde ponctuelle, mais c'est l'usage : la plupart des familles appliquent une majoration de 10 % à 25 % pour les gardes de nuit, le dimanche et les jours fériés.",
  },
  {
    q: "Comment déclarer et payer sa baby-sitter ?",
    a: "Deux dispositifs existent : le CESU (chèque emploi service universel) pour les enfants de plus de 6 ans et Pajemploi pour les moins de 6 ans. La déclaration ouvre droit à un crédit d'impôt et protège les deux parties.",
  },
  {
    q: "Combien coûte une garde d'enfants sur Click&Gard ?",
    a: "Click&Gard est 100 % gratuit pour les parents et ne prend aucune commission sur la prestation : vous payez uniquement le tarif horaire affiché sur le profil du professionnel, directement à la personne qui garde vos enfants.",
  },
];

const TarifsBabySitting = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tarifs baby-sitting", item: `${SITE}${PATH}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Garde d'enfants à domicile",
      serviceType: "Baby-sitting",
      areaServed: { "@type": "Country", name: "France" },
      provider: { "@type": "Organization", name: "Click&Gard", url: SITE },
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "EUR",
        lowPrice: "11",
        highPrice: "18",
        unitText: "heure",
      },
    },
  ];

  return (
    <div className="min-h-screen bg-immersive">
      <Seo
        title="Tarif baby-sitter : prix horaire de la garde d'enfants"
        description="Tarif baby-sitter en France : 11 € à 18 € de l'heure selon la ville et le type de garde. Prix par ville, majorations soirée et week-end, CESU et crédit d'impôt."
        path={PATH}
        jsonLd={jsonLd}
      />
      <Navbar />

      <section className="relative overflow-hidden py-16 md:py-24 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            Tarif baby-sitter : combien coûte une garde d'enfants ?
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8">
            En France, une baby-sitter est rémunérée entre <strong>11 € et 18 € de l'heure</strong> selon la ville, l'expérience et
            le type de garde. Voici les fourchettes réelles constatées, ville par ville et situation par situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search">
              <Button size="lg" variant="neon" className="text-base font-semibold">
                <Search className="w-5 h-5" />
                Voir les tarifs près de chez moi
              </Button>
            </Link>
            <Link to="/auth?tab=register">
              <Button size="lg" variant="outline" className="text-base font-semibold">Créer un compte gratuit</Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold mb-8 flex items-center gap-3">
            <Calculator className="w-7 h-7 text-primary" /> Tarif horaire selon le type de garde
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border/50">
            <table className="w-full text-left text-sm">
              <thead className="bg-card/60">
                <tr>
                  <th scope="col" className="p-4 font-semibold">Type de garde</th>
                  <th scope="col" className="p-4 font-semibold">Tarif horaire net</th>
                  <th scope="col" className="p-4 font-semibold hidden md:table-cell">Détail</th>
                </tr>
              </thead>
              <tbody>
                {SITUATIONS.map((s) => (
                  <tr key={s.label} className="border-t border-border/40">
                    <td className="p-4 font-medium">{s.label}</td>
                    <td className="p-4 text-primary font-semibold whitespace-nowrap">{s.rate}</td>
                    <td className="p-4 text-muted-foreground hidden md:table-cell">{s.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 bg-card/30 border-y border-border/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold mb-8 flex items-center gap-3">
            <MapPin className="w-7 h-7 text-primary" /> Prix d'une baby-sitter par ville
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {CITY_RATES.map((c) => (
              <Link
                key={c.slug}
                to={`/baby-sitter/${c.slug}`}
                className="flex items-center justify-between rounded-xl border border-border/50 bg-card/50 p-4 hover:border-primary/40 transition-all"
              >
                <span className="font-medium">Baby-sitter à {c.city}</span>
                <span className="text-primary font-semibold">{c.low} € – {c.high} € / h</span>
              </Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 justify-center mt-8">
            {SEO_CITIES.slice(8, 24).map((c) => (
              <Link
                key={c.slug}
                to={`/baby-sitter/${c.slug}`}
                className="px-4 py-2 rounded-full bg-card/50 border border-border/50 text-sm hover:border-primary/40 hover:text-primary transition-all"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto space-y-6 text-foreground/90">
          <h2 className="text-3xl font-display font-bold">Ce qui fait varier le prix d'une garde d'enfants</h2>
          <p>
            Trois facteurs expliquent l'essentiel des écarts de tarif : <strong>la ville</strong> (le coût de la vie parisien tire
            les prix vers le haut), <strong>l'expérience et les diplômes</strong> du professionnel (CAP Petite Enfance, BAFA,
            formation aux premiers secours) et <strong>les contraintes de la garde</strong> (horaires tardifs, nombre d'enfants,
            enfant en bas âge, trajets ou tâches supplémentaires).
          </p>
          <p>
            Le tarif affiché est presque toujours un <strong>net horaire</strong>. Si vous déclarez la garde via le CESU ou
            Pajemploi, prévoyez les cotisations employeur en plus — largement compensées par le crédit d'impôt auquel la garde
            d'enfants à domicile peut ouvrir droit.
          </p>
          <p>
            Sur Click&Gard, chaque professionnel affiche son tarif horaire sur son profil, avant toute réservation. La plateforme
            est <strong>gratuite pour les parents</strong> et ne prélève <strong>aucune commission</strong> : le montant convenu va
            intégralement à la personne qui garde vos enfants.
          </p>
          <p className="flex items-center gap-2 text-muted-foreground">
            <BadgeCheck className="w-5 h-5 text-primary shrink-0" />
            Tous les profils sont vérifiés (identité et documents) avant d'être visibles par les familles.
          </p>
          <p className="text-sm text-muted-foreground">
            Pour aller plus loin :{" "}
            <Link to="/blog/tarifs-baby-sitting-france-2026" className="text-primary underline">
              guide complet des tarifs de baby-sitting
            </Link>{" "}
            ·{" "}
            <Link to="/blog/demarches-pajemploi-cesu-baby-sitter" className="text-primary underline">
              démarches Pajemploi et CESU
            </Link>{" "}
            ·{" "}
            <Link to="/baby-sitter" className="text-primary underline">
              trouver une baby-sitter
            </Link>
          </p>
        </div>
      </section>

      <section className="py-12 px-4 bg-card/30 border-t border-border/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-center mb-10">Questions fréquentes sur les tarifs</h2>
          <div className="space-y-4">
            {FAQ.map((f) => (
              <article key={f.q} className="rounded-xl border border-border/50 bg-card/50 p-6">
                <h3 className="font-semibold text-lg mb-2 text-foreground">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </article>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/search">
              <Button size="lg" variant="neon" className="text-base font-semibold">
                <Search className="w-5 h-5" />
                Comparer les tarifs près de chez moi
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TarifsBabySitting;
